import { useMemo } from 'react';
import { useGameState } from '../state';

/**
 * Zentraler Hook für den aktuell aktiven Spieler-Index.
 *
 * Im Multiplayer: Immer myPlayerIndex
 * Im Test-Mode: Kontextabhängig (exchangeCurrentPlayer, shopCurrentPlayer, currentPlayer)
 *
 * Dieser Hook ersetzt alle verstreuten `isLocalTestMode ? X : Y` Checks.
 */
export function useActivePlayer() {
  const globalState = useGameState();

  return useMemo(() => {
    const {
      isLocalTestMode,
      myPlayerIndex,
      currentPlayer,
      exchangeState,
      exchangeCurrentPlayer,
      sealDistribution,
      showShop,
      shopCurrentPlayer,
      testModeViewPlayer,
    } = globalState;

    // Multiplayer: Immer der eigene Index
    if (!isLocalTestMode) {
      return {
        playerIndex: myPlayerIndex ?? 0,
        isTestMode: false,
        context: 'multiplayer',
      };
    }

    // Test-Mode: Use testModeViewPlayer if set, otherwise follow context
    let playerIndex;
    let context = 'playing';

    // If testModeViewPlayer is explicitly set, use it
    if (testModeViewPlayer !== null && testModeViewPlayer !== undefined) {
      playerIndex = testModeViewPlayer;
      context = 'viewing';
    }
    // During seal distribution phase
    else if (sealDistribution?.active) {
      playerIndex = exchangeCurrentPlayer ?? currentPlayer ?? 0;
      context = 'sealDistribution';
    }
    // Exchange-Phase (giving/receiving) hat eigenen Current Player
    else if (exchangeState) {
      playerIndex = exchangeCurrentPlayer ?? 0;
      context = 'exchange';
    }
    // Shop-Phase hat eigenen Current Player
    else if (showShop) {
      playerIndex = shopCurrentPlayer ?? 0;
      context = 'shop';
    }
    // Default: follow currentPlayer
    else {
      playerIndex = currentPlayer ?? 0;
    }

    return {
      playerIndex,
      isTestMode: true,
      context,
    };
  }, [globalState]);
}

/**
 * Convenience Hook: Nur der Player-Index
 */
export function useActivePlayerIndex() {
  return useActivePlayer().playerIndex;
}

/**
 * Convenience Hook: Ist Test-Mode aktiv?
 */
export function useIsTestMode() {
  return useActivePlayer().isTestMode;
}
